import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FilePdf, CheckCircle, CurrencyDollar, Ticket, AddressBook, GearSix, Megaphone } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { ReportsConfig } from "./ReportsConfig";

const RANGE_OPTIONS = [
  { value: "weekly", label: "Semanal (7 dias)" },
  { value: "biweekly", label: "Quincenal (15 dias)" },
  { value: "monthly", label: "Mensual (30 dias)" },
];

const MONTHS = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" }, { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" }, { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ============= TICKETS REPORT TAB =============
const TicketsReport = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const { token } = useAuth();
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.get(`${API}/reports/monthly-pdf?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      downloadBlob(res.data, `reporte_tickets_${year}_${String(month).padStart(2, "0")}.pdf`);
      toast.success("Reporte de Tickets descargado");
    } catch { toast.error("Error al generar reporte"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6" data-testid="tickets-report-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-slate-700 text-sm">Mes</Label>
          <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
            <SelectTrigger className="mt-1" data-testid="report-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-700 text-sm">Ano</Label>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="mt-1" data-testid="report-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={handleGenerate} disabled={generating} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="generate-tickets-pdf">
            <FilePdf size={18} className="mr-2" />{generating ? "Generando..." : "Generar PDF"}
          </Button>
        </div>
      </div>
      <ReportIncludes items={[
        "Resumen general de tickets del mes",
        "Desglose por ingeniero con tasas de resolucion",
        "Lista detallada de tickets (hasta 50)",
        "Estadisticas de nuevos, en proceso y resueltos",
      ]} />
    </div>
  );
};

// ============= QUOTATIONS REPORT TAB =============
const QuotationsReport = () => {
  const [range, setRange] = useState("monthly");
  const [generating, setGenerating] = useState(false);
  const { token } = useAuth();

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.get(`${API}/reports/quotations-pdf?range_type=${range}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      downloadBlob(res.data, `reporte_cotizaciones_${range}.pdf`);
      toast.success("Reporte de Cotizaciones descargado");
    } catch { toast.error("Error al generar reporte"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6" data-testid="quotations-report-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-700 text-sm">Rango de Fechas</Label>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="mt-1" data-testid="quotation-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={handleGenerate} disabled={generating} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="generate-quotations-pdf">
            <FilePdf size={18} className="mr-2" />{generating ? "Generando..." : "Generar PDF"}
          </Button>
        </div>
      </div>
      <ReportIncludes items={[
        "Resumen general de cotizaciones por rango de fechas",
        "Enviados por semana / quincena / mensual",
        "Desglose de valor cotizado por vendedor/comercial",
        "Tasa de aprobacion y cierre de ventas",
        "Desglose por vendedores con tasas de cierre",
        "Lista detallada de cotizaciones (hasta 50)",
        "Estadisticas de estatus",
      ]} />
    </div>
  );
};

// ============= CONTACTS REPORT TAB =============
const ContactsReport = () => {
  const [range, setRange] = useState("monthly");
  const [generating, setGenerating] = useState(false);
  const { token } = useAuth();

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.get(`${API}/reports/contacts-pdf?range_type=${range}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      downloadBlob(res.data, `reporte_contactos_${range}.pdf`);
      toast.success("Reporte de Contactos descargado");
    } catch { toast.error("Error al generar reporte"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6" data-testid="contacts-report-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-700 text-sm">Rango de Fechas</Label>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="mt-1" data-testid="contacts-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={handleGenerate} disabled={generating} className="w-full bg-violet-700 hover:bg-violet-800" data-testid="generate-contacts-pdf">
            <FilePdf size={18} className="mr-2" />{generating ? "Generando..." : "Generar PDF"}
          </Button>
        </div>
      </div>
      <ReportIncludes items={[
        "Resumen general de altas de contactos por rango",
        "Desglose por vendedores con tasas de cierre de ventas",
        "Resumen total de contactos por estatus y cierre",
        "Resumen de rendimiento por vendedor",
        "Contactos por ciudad",
      ]} />
    </div>
  );
};

// ============= MARKETING REPORT TAB =============
const MarketingReport = () => {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const { token } = useAuth();
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let url = `${API}/reports/marketing-budget-pdf?year=${year}`;
      if (month && month !== "all") url += `&month=${month}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      const fn = month
        ? `reporte_marketing_${year}_${String(month).padStart(2, "0")}.pdf`
        : `reporte_marketing_${year}.pdf`;
      downloadBlob(res.data, fn);
      toast.success("Reporte de Marketing descargado");
    } catch { toast.error("Error al generar reporte"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6" data-testid="marketing-report-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-slate-700 text-sm">Mes (opcional)</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="mt-1" data-testid="mkt-report-month-select"><SelectValue placeholder="Todos (anual)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos (Anual)</SelectItem>
              {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-700 text-sm">Ano</Label>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="mt-1" data-testid="mkt-report-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end md:col-span-2">
          <Button onClick={handleGenerate} disabled={generating} className="w-full bg-pink-700 hover:bg-pink-800" data-testid="generate-marketing-pdf">
            <FilePdf size={18} className="mr-2" />{generating ? "Generando..." : "Generar PDF"}
          </Button>
        </div>
      </div>
      <ReportIncludes items={[
        "Resumen general de presupuesto total, gastado y restante",
        "Tabla de presupuesto por mes con porcentaje de uso",
        "Desglose de gastos por categoria con porcentaje del total",
        "Detalle de cada gasto: concepto, categoria, monto, fecha y responsable",
        "Si se selecciona un mes, el detalle se filtra solo a ese mes",
      ]} />
    </div>
  );
};

// ============= SHARED COMPONENTS =============
const ReportIncludes = ({ items }) => (
  <div className="border border-slate-200 rounded-lg p-5 bg-slate-50/80">
    <h3 className="font-semibold text-slate-800 text-sm mb-3">El reporte incluye:</h3>
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
          <CheckCircle size={15} className="text-emerald-600 shrink-0" weight="duotone" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

// ============= MAIN MODULE =============
const TABS = [
  { id: "tickets", label: "Tickets", icon: Ticket, color: "blue" },
  { id: "quotations", label: "Cotizaciones", icon: CurrencyDollar, color: "emerald" },
  { id: "contacts", label: "Contactos", icon: AddressBook, color: "violet" },
  { id: "marketing", label: "Marketing", icon: Megaphone, color: "pink" },
  { id: "config", label: "Configuracion", icon: GearSix, color: "slate", adminOnly: true },
];

const MonthlyReports = () => {
  const [activeTab, setActiveTab] = useState("tickets");
  const { user } = useAuth();

  const visibleTabs = TABS.filter(t => !t.adminOnly || user?.role === "admin");

  return (
    <div data-testid="monthly-reports">
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Reportes</h1>
      <p className="text-slate-500 text-sm mb-6">Genera reportes en PDF por tipo y rango de fechas.</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200" data-testid="report-tabs">
        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const colorMap = { blue: ["#2563eb", "#1d4ed8"], emerald: ["#059669", "#047857"], violet: ["#7c3aed", "#6d28d9"], pink: ["#db2777", "#be185d"], slate: ["#475569", "#334155"] };
          const [borderC, textC] = colorMap[tab.color] || colorMap.blue;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`report-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${
                isActive
                  ? "border-current"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
              style={isActive ? { borderBottomColor: borderC, color: textC } : {}}>
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {activeTab === "tickets" && <TicketsReport />}
        {activeTab === "quotations" && <QuotationsReport />}
        {activeTab === "contacts" && <ContactsReport />}
        {activeTab === "marketing" && <MarketingReport />}
        {activeTab === "config" && <ReportsConfig />}
      </div>
    </div>
  );
};

export { MonthlyReports };
